"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.getAdvancedOrderNumeratorDenominator = exports.generateFulfillOrdersFulfillments = exports.fulfillAvailableOrders = exports.validateAndSanitizeFromOrderStatus = exports.fulfillStandardOrder = exports.fulfillBasicOrder = exports.shouldUseBasicFulfill = void 0;
const ethers_1 = require("ethers");
const constants_1 = require("../constants");
const approval_1 = require("./approval");
const balanceAndApprovalCheck_1 = require("./balanceAndApprovalCheck");
const criteria_1 = require("./criteria");
const gcd_1 = require("./gcd");
const item_1 = require("./item");
const order_1 = require("./order");
const usecase_1 = require("./usecase");
/**
 * We should use basic fulfill order if the order adheres to the following criteria:
 * 1. The order should not be partially filled.
 * 2. The order only contains a single offer item and contains at least one consideration item
 * 3. The order does not offer an item with Ether (or other native tokens) as its item type.
 * 4. The order only contains a single ERC721 or ERC1155 item and that item is not criteria-based
 * 5. All other items have the same Native or ERC20 item type and token
 * 6. All items have the same startAmount and endAmount
 * 7. First consideration item must contain the offerer as the recipient
 * 8. If the order has multiple consideration items and all consideration items other than the
 *    first consideration item have the same item type as the offered item, the offered item
 *    amount is not less than the sum of all consideration item amounts excluding the
 *    first consideration item amount
 * 9. The token on native currency items needs to be set to the null address and the identifier on
 *    currencies needs to be zero, and the amounts on the 721 item need to be 1
 */
const shouldUseBasicFulfill = ({ offer, consideration, offerer }, totalFilled) => {
    // 1. The order must not be partially filled
    if (totalFilled !== 0n) {
        return false;
    }
    // 2. Must be single offer and at least one consideration
    if (offer.length > 1 || consideration.length === 0) {
        return false;
    }
    const allItems = [...offer, ...consideration];
    const nfts = allItems.filter(({ itemType }) => [constants_1.ItemType.ERC721, constants_1.ItemType.ERC1155].includes(itemType));
    const nftsWithCriteria = allItems.filter(({ itemType }) => (0, item_1.isCriteriaItem)(itemType));
    const offersNativeCurrency = (0, item_1.isNativeCurrencyItem)(offer[0]);
    // 3. The order does not offer an item with Ether (or other native tokens) as its item type.
    if (offersNativeCurrency) {
        return false;
    }
    // 4. The order only contains a single ERC721 or ERC1155 item and that item is not criteria-based
    if (nfts.length !== 1 || nftsWithCriteria.length !== 0) {
        return false;
    }
    // 5. All currencies need to have the same address and item type (Native, ERC20)
    if (!(0, order_1.areAllCurrenciesSame)({ offer, consideration })) {
        return false;
    }
    // 6. All individual items need to have the same startAmount and endAmount
    const differentStartAndEndAmount = allItems.some(({ startAmount, endAmount }) => startAmount !== endAmount);
    if (differentStartAndEndAmount) {
        return false;
    }
    const [firstConsideration, ...restConsideration] = consideration;
    // 7. First consideration item must contain the offerer as the recipient
    const firstConsiderationRecipientIsNotOfferer = firstConsideration.recipient.toLowerCase() !== offerer.toLowerCase();
    if (firstConsiderationRecipientIsNotOfferer) {
        return false;
    }
    // 8. If the order has multiple consideration items and all consideration items other than the
    // first consideration item have the same item type as the offered item, the offered item
    // amount is not less than the sum of all consideration item amounts excluding the
    // first consideration item amount
    if (consideration.length > 1 &&
        restConsideration.every((item) => item.itemType === offer[0].itemType) &&
        (0, order_1.totalItemsAmount)(restConsideration).endAmount > BigInt(offer[0].endAmount)) {
        return false;
    }
    const currencies = allItems.filter(item_1.isCurrencyItem);
    //  9. The token on native currency items needs to be set to the null address and the identifier on
    //  currencies needs to be zero, and the amounts on the 721 item need to be 1
    const nativeCurrencyIsZeroAddress = currencies
        .filter(({ itemType }) => itemType === constants_1.ItemType.NATIVE)
        .every(({ token }) => token === ethers_1.ethers.ZeroAddress);
    const currencyIdentifiersAreZero = currencies.every(({ identifierOrCriteria }) => BigInt(identifierOrCriteria) === 0n);
    const erc721sAreSingleAmount = nfts
        .filter(({ itemType }) => itemType === constants_1.ItemType.ERC721)
        .every(({ endAmount }) => endAmount === "1");
    return (nativeCurrencyIsZeroAddress &&
        currencyIdentifiersAreZero &&
        erc721sAreSingleAmount);
};
exports.shouldUseBasicFulfill = shouldUseBasicFulfill;
const offerAndConsiderationFulfillmentMapping = {
    [constants_1.ItemType.ERC20]: {
        [constants_1.ItemType.ERC721]: constants_1.BasicOrderRouteType.ERC721_TO_ERC20,
        [constants_1.ItemType.ERC1155]: constants_1.BasicOrderRouteType.ERC1155_TO_ERC20,
    },
    [constants_1.ItemType.ERC721]: {
        [constants_1.ItemType.NATIVE]: constants_1.BasicOrderRouteType.ETH_TO_ERC721,
        [constants_1.ItemType.ERC20]: constants_1.BasicOrderRouteType.ERC20_TO_ERC721,
    },
    [constants_1.ItemType.ERC1155]: {
        [constants_1.ItemType.NATIVE]: constants_1.BasicOrderRouteType.ETH_TO_ERC1155,
        [constants_1.ItemType.ERC20]: constants_1.BasicOrderRouteType.ERC20_TO_ERC1155,
    },
};
function fulfillBasicOrder({ order, seaportContract, offererBalancesAndApprovals, fulfillerBalancesAndApprovals, timeBasedItemParams, offererOperator, fulfillerOperator, signer, tips = [], conduitKey = constants_1.NO_CONDUIT, domain, overrides, }, exactApproval) {
    const { offer, consideration } = order.parameters;
    const considerationIncludingTips = [...consideration, ...tips];
    const offerItem = offer[0];
    const [forOfferer, ...forAdditionalRecipients] = considerationIncludingTips;
    const basicOrderRouteType = offerAndConsiderationFulfillmentMapping[offerItem.itemType]?.[forOfferer.itemType];
    if (basicOrderRouteType === undefined) {
        throw new Error("Order parameters did not result in a valid basic fulfillment");
    }
    const additionalRecipients = forAdditionalRecipients.map(({ startAmount, recipient }) => ({
        amount: startAmount,
        recipient,
    }));
    const considerationWithoutOfferItemType = considerationIncludingTips.filter((item) => item.itemType !== offer[0].itemType);
    const totalNativeAmount = (0, item_1.getSummedTokenAndIdentifierAmounts)({
        items: considerationWithoutOfferItemType,
        criterias: [],
        timeBasedItemParams: {
            ...timeBasedItemParams,
            isConsiderationItem: true,
        },
    })[ethers_1.ethers.ZeroAddress]?.["0"];
    const insufficientApprovals = (0, balanceAndApprovalCheck_1.validateBasicFulfillBalancesAndApprovals)({
        offer,
        consideration: considerationIncludingTips,
        offererBalancesAndApprovals,
        fulfillerBalancesAndApprovals,
        timeBasedItemParams,
        offererOperator,
        fulfillerOperator,
    });
    const basicOrderParameters = {
        offerer: order.parameters.offerer,
        offererConduitKey: order.parameters.conduitKey,
        zone: order.parameters.zone,
        //  Note the use of a "basicOrderType" enum;
        //  this represents both the usual order type as well as the "route"
        //  of the basic order (a simple derivation function for the basic order
        //  type is `basicOrderType = orderType + (4 * basicOrderRoute)`.)
        basicOrderType: order.parameters.orderType + 4 * basicOrderRouteType,
        offerToken: offerItem.token,
        offerIdentifier: offerItem.identifierOrCriteria,
        offerAmount: offerItem.endAmount,
        considerationToken: forOfferer.token,
        considerationIdentifier: forOfferer.identifierOrCriteria,
        considerationAmount: forOfferer.endAmount,
        startTime: order.parameters.startTime,
        endTime: order.parameters.endTime,
        salt: order.parameters.salt,
        totalOriginalAdditionalRecipients: order.parameters.consideration.length - 1,
        signature: order.signature,
        fulfillerConduitKey: conduitKey,
        additionalRecipients,
        zoneHash: order.parameters.zoneHash,
    };
    overrides = { ...overrides, value: totalNativeAmount };
    const approvalActions = (0, approval_1.getApprovalActions)(insufficientApprovals, exactApproval, signer);
    const exchangeAction = {
        type: "exchange",
        transactionMethods: (0, usecase_1.getTransactionMethods)(signer, seaportContract, "fulfillBasicOrder", [basicOrderParameters, overrides], domain),
    };
    const actions = [...approvalActions, exchangeAction];
    return {
        actions,
        executeAllActions: () => (0, usecase_1.executeAllActions)(actions),
    };
}
exports.fulfillBasicOrder = fulfillBasicOrder;
function fulfillStandardOrder({ order, unitsToFill = 0, totalSize, totalFilled, offerCriteria, considerationCriteria, tips = [], extraData, seaportContract, offererBalancesAndApprovals, fulfillerBalancesAndApprovals, offererOperator, fulfillerOperator, timeBasedItemParams, conduitKey, recipientAddress, signer, domain, overrides, }, exactApproval) {
    // If we are supplying units to fill, we adjust the order by the minimum of the amount to fill and
    // the remaining order left to be fulfilled
    const orderWithAdjustedFills = unitsToFill
        ? (0, order_1.mapOrderAmountsFromUnitsToFill)(order, {
            unitsToFill,
            totalSize,
        })
        : // Else, we adjust the order by the remaining order left to be fulfilled
            (0, order_1.mapOrderAmountsFromFilledStatus)(order, {
                totalFilled,
                totalSize,
            });
    let adjustedTips = [];
    if (tips.length > 0) {
        adjustedTips = (0, order_1.adjustTipsForPartialFills)(tips, unitsToFill, totalSize);
    }
    const { parameters: { offer, consideration }, } = orderWithAdjustedFills;
    const considerationIncludingTips = [...consideration, ...adjustedTips];
    const offerCriteriaItems = offer.filter(({ itemType }) => (0, item_1.isCriteriaItem)(itemType));
    const considerationCriteriaItems = considerationIncludingTips.filter(({ itemType }) => (0, item_1.isCriteriaItem)(itemType));
    const hasCriteriaItems = offerCriteriaItems.length > 0 || considerationCriteriaItems.length > 0;
    if (offerCriteriaItems.length !== offerCriteria.length ||
        considerationCriteriaItems.length !== considerationCriteria.length) {
        throw new Error("You must supply the appropriate criterias for criteria based items");
    }
    const totalNativeAmount = (0, item_1.getSummedTokenAndIdentifierAmounts)({
        items: considerationIncludingTips,
        criterias: considerationCriteria,
        timeBasedItemParams: {
            ...timeBasedItemParams,
            isConsiderationItem: true,
        },
    })[ethers_1.ethers.ZeroAddress]?.["0"];
    const insufficientApprovals = (0, balanceAndApprovalCheck_1.validateStandardFulfillBalancesAndApprovals)({
        offer,
        consideration: considerationIncludingTips,
        offerCriteria,
        considerationCriteria,
        offererBalancesAndApprovals,
        fulfillerBalancesAndApprovals,
        timeBasedItemParams,
        offererOperator,
        fulfillerOperator,
    });
    overrides = { ...overrides, value: totalNativeAmount };
    const approvalActions = (0, approval_1.getApprovalActions)(insufficientApprovals, exactApproval, signer);
    const isGift = recipientAddress !== ethers_1.ethers.ZeroAddress;
    const useAdvanced = Boolean(unitsToFill) || hasCriteriaItems || isGift;
    const orderAccountingForTips = {
        ...order,
        parameters: {
            ...order.parameters,
            consideration: [...order.parameters.consideration, ...tips],
            totalOriginalConsiderationItems: consideration.length,
        },
    };
    const { numerator, denominator } = (0, exports.getAdvancedOrderNumeratorDenominator)(order, unitsToFill);
    const exchangeAction = {
        type: "exchange",
        transactionMethods: useAdvanced
            ? (0, usecase_1.getTransactionMethods)(signer, seaportContract, "fulfillAdvancedOrder", [
                {
                    ...orderAccountingForTips,
                    numerator,
                    denominator,
                    extraData: extraData ?? "0x",
                },
                hasCriteriaItems
                    ? (0, criteria_1.generateCriteriaResolvers)({
                        orders: [order],
                        offerCriterias: [offerCriteria],
                        considerationCriterias: [considerationCriteria],
                    })
                    : [],
                conduitKey,
                recipientAddress,
                overrides,
            ], domain)
            : (0, usecase_1.getTransactionMethods)(signer, seaportContract, "fulfillOrder", [orderAccountingForTips, conduitKey, overrides], domain),
    };
    const actions = [...approvalActions, exchangeAction];
    return {
        actions,
        executeAllActions: () => (0, usecase_1.executeAllActions)(actions),
    };
}
exports.fulfillStandardOrder = fulfillStandardOrder;
function validateAndSanitizeFromOrderStatus(order, orderStatus) {
    const { isValidated, isCancelled, totalFilled, totalSize } = orderStatus;
    if (totalSize > 0n && totalFilled / totalSize === 1n) {
        throw new Error("The order you are trying to fulfill is already filled");
    }
    if (isCancelled) {
        throw new Error("The order you are trying to fulfill is cancelled");
    }
    if (isValidated) {
        // If the order is already validated, manually wipe the signature off of the order to save gas
        return { parameters: { ...order.parameters }, signature: "0x" };
    }
    return order;
}
exports.validateAndSanitizeFromOrderStatus = validateAndSanitizeFromOrderStatus;
function fulfillAvailableOrders({ ordersMetadata, seaportContract, fulfillerBalancesAndApprovals, fulfillerOperator, currentBlockTimestamp, ascendingAmountTimestampBuffer, conduitKey, signer, recipientAddress, exactApproval, domain, }) {
    const sanitizedOrdersMetadata = ordersMetadata.map((orderMetadata) => ({
        ...orderMetadata,
        order: validateAndSanitizeFromOrderStatus(orderMetadata.order, orderMetadata.orderStatus),
    }));
    const ordersMetadataWithAdjustedFills = sanitizedOrdersMetadata.map((orderMetadata) => ({
        ...orderMetadata,
        // If we are supplying units to fill, we adjust the order by the minimum of the amount to fill and
        // the remaining order left to be fulfilled
        order: orderMetadata.unitsToFill
            ? (0, order_1.mapOrderAmountsFromUnitsToFill)(orderMetadata.order, {
                unitsToFill: orderMetadata.unitsToFill,
                totalSize: orderMetadata.orderStatus.totalSize,
            })
            : // Else, we adjust the order by the remaining order left to be fulfilled
                (0, order_1.mapOrderAmountsFromFilledStatus)(orderMetadata.order, {
                    totalFilled: orderMetadata.orderStatus.totalFilled,
                    totalSize: orderMetadata.orderStatus.totalSize,
                }),
    }));
    let totalNativeAmount = 0n;
    const totalInsufficientApprovals = [];
    const criteriaOffersAndConsiderations = sanitizedOrdersMetadata
        .flatMap((orderMetadata) => [
        orderMetadata.order.parameters.offer,
        orderMetadata.order.parameters.consideration,
    ])
        .flat()
        .filter(({ itemType }) => (0, item_1.isCriteriaItem)(itemType));
    const hasCriteriaItems = criteriaOffersAndConsiderations.length > 0;
    const addApprovalIfNeeded = (orderInsufficientApprovals) => {
        orderInsufficientApprovals.forEach((insufficientApproval) => {
            if (!totalInsufficientApprovals.find((approval) => approval.token === insufficientApproval.token)) {
                totalInsufficientApprovals.push(insufficientApproval);
            }
        });
    };
    ordersMetadataWithAdjustedFills.forEach(({ order, tips, offerCriteria, considerationCriteria, offererBalancesAndApprovals, offererOperator, }) => {
        const considerationIncludingTips = [
            ...order.parameters.consideration,
            ...tips,
        ];
        const timeBasedItemParams = {
            startTime: order.parameters.startTime,
            endTime: order.parameters.endTime,
            currentBlockTimestamp,
            ascendingAmountTimestampBuffer,
            isConsiderationItem: true,
        };
        totalNativeAmount =
            totalNativeAmount +
                ((0, item_1.getSummedTokenAndIdentifierAmounts)({
                    items: considerationIncludingTips,
                    criterias: considerationCriteria,
                    timeBasedItemParams,
                })[ethers_1.ethers.ZeroAddress]?.["0"] ?? 0n);
        const insufficientApprovals = (0, balanceAndApprovalCheck_1.validateStandardFulfillBalancesAndApprovals)({
            offer: order.parameters.offer,
            consideration: considerationIncludingTips,
            offerCriteria,
            considerationCriteria,
            offererBalancesAndApprovals,
            fulfillerBalancesAndApprovals,
            timeBasedItemParams,
            offererOperator,
            fulfillerOperator,
        });
        const offerCriteriaItems = order.parameters.offer.filter(({ itemType }) => (0, item_1.isCriteriaItem)(itemType));
        const considerationCriteriaItems = considerationIncludingTips.filter(({ itemType }) => (0, item_1.isCriteriaItem)(itemType));
        if (offerCriteriaItems.length !== offerCriteria.length ||
            considerationCriteriaItems.length !== considerationCriteria.length) {
            throw new Error("You must supply the appropriate criterias for criteria based items");
        }
        addApprovalIfNeeded(insufficientApprovals);
    });
    const overrides = { value: totalNativeAmount };
    const approvalActions = (0, approval_1.getApprovalActions)(totalInsufficientApprovals, exactApproval, signer);
    const advancedOrdersWithTips = sanitizedOrdersMetadata.map(({ order, unitsToFill = 0, tips, extraData }) => {
        const { numerator, denominator } = (0, exports.getAdvancedOrderNumeratorDenominator)(order, unitsToFill);
        const considerationIncludingTips = [
            ...order.parameters.consideration,
            ...tips,
        ];
        return {
            ...order,
            parameters: {
                ...order.parameters,
                consideration: considerationIncludingTips,
                totalOriginalConsiderationItems: order.parameters.consideration.length,
            },
            numerator,
            denominator,
            extraData,
        };
    });
    const { offerFulfillments, considerationFulfillments } = generateFulfillOrdersFulfillments(ordersMetadata);
    const exchangeAction = {
        type: "exchange",
        transactionMethods: (0, usecase_1.getTransactionMethods)(signer, seaportContract, "fulfillAvailableAdvancedOrders", [
            advancedOrdersWithTips,
            hasCriteriaItems
                ? (0, criteria_1.generateCriteriaResolvers)({
                    orders: ordersMetadata.map(({ order }) => order),
                    offerCriterias: ordersMetadata.map(({ offerCriteria }) => offerCriteria),
                    considerationCriterias: ordersMetadata.map(({ considerationCriteria }) => considerationCriteria),
                })
                : [],
            offerFulfillments,
            considerationFulfillments,
            conduitKey,
            recipientAddress,
            advancedOrdersWithTips.length,
            overrides,
        ], domain),
    };
    const actions = [...approvalActions, exchangeAction];
    return {
        actions,
        executeAllActions: () => (0, usecase_1.executeAllActions)(actions),
    };
}
exports.fulfillAvailableOrders = fulfillAvailableOrders;
function generateFulfillOrdersFulfillments(ordersMetadata) {
    const hashAggregateKey = ({ sourceOrDestination, operator = "", token, identifier, }) => `${sourceOrDestination}-${operator}-${token}-${identifier}`;
    const offerAggregatedFulfillments = {};
    const considerationAggregatedFulfillments = {};
    ordersMetadata.forEach(({ order, offererOperator, offerCriteria }, orderIndex) => {
        const itemToCriteria = (0, criteria_1.getItemToCriteriaMap)(order.parameters.offer, offerCriteria);
        return order.parameters.offer.forEach((item, itemIndex) => {
            const aggregateKey = `${hashAggregateKey({
                sourceOrDestination: order.parameters.offerer,
                operator: offererOperator,
                token: item.token,
                identifier: itemToCriteria.get(item)?.identifier ?? item.identifierOrCriteria,
                // We tack on the index to ensure that erc721s can never be aggregated and instead must be in separate arrays
            })}${(0, item_1.isErc721Item)(item.itemType) ? itemIndex : ""}`;
            offerAggregatedFulfillments[aggregateKey] = [
                ...(offerAggregatedFulfillments[aggregateKey] ?? []),
                { orderIndex, itemIndex },
            ];
        });
    });
    ordersMetadata.forEach(({ order, considerationCriteria, tips }, orderIndex) => {
        const itemToCriteria = (0, criteria_1.getItemToCriteriaMap)(order.parameters.consideration, considerationCriteria);
        return [...order.parameters.consideration, ...tips].forEach((item, itemIndex) => {
            const aggregateKey = `${hashAggregateKey({
                sourceOrDestination: item.recipient,
                token: item.token,
                identifier: itemToCriteria.get(item)?.identifier ?? item.identifierOrCriteria,
                // We tack on the index to ensure that erc721s can never be aggregated and instead must be in separate arrays
            })}${(0, item_1.isErc721Item)(item.itemType) ? itemIndex : ""}`;
            considerationAggregatedFulfillments[aggregateKey] = [
                ...(considerationAggregatedFulfillments[aggregateKey] ??
                    []),
                { orderIndex, itemIndex },
            ];
        });
    });
    return {
        offerFulfillments: Object.values(offerAggregatedFulfillments),
        considerationFulfillments: Object.values(considerationAggregatedFulfillments),
    };
}
exports.generateFulfillOrdersFulfillments = generateFulfillOrdersFulfillments;
const getAdvancedOrderNumeratorDenominator = (order, unitsToFill) => {
    // Used for advanced order cases
    const maxUnits = (0, item_1.getMaximumSizeForOrder)(order);
    // Reduce the numerator/denominator as optimization
    let numerator = 1n;
    let denominator = 1n;
    if (unitsToFill) {
        const unitsGcd = (0, gcd_1.gcd)(BigInt(unitsToFill), maxUnits);
        numerator = BigInt(unitsToFill) / unitsGcd;
        denominator = maxUnits / unitsGcd;
    }
    return { numerator, denominator };
};
exports.getAdvancedOrderNumeratorDenominator = getAdvancedOrderNumeratorDenominator;
//# sourceMappingURL=fulfill.js.map